import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { unitId as brandUnitId, actionId } from '@/annotations'
import { Button } from '@/components/Button'
import { Note } from '@/components/Note'
import { Pager } from '@/components/Pager'
import { GridTable } from '@/components/GridTable'
import { WizardProse, WizardButtons, WizardHeading } from '@/components/WizardParts'
import { useCursorPager } from '@/data/useCursorPager'
import { getApiClient, ApiProblemError } from '@/data/apiClient'
import { UnexpectedRefusal, isUnexpectedRefusal } from '@/screens/auth-shared/Refusals'
import { useActiveOrgId, useActiveWorkspaceId } from '@/data/store'
import { formatJobTime, formatBytes } from '@/screens/shared/jobFormatting'
import type { components } from '@/api/generated/schema'
import type { Problem } from '@/data/apiClient'
import type { RestoreWizardConfig, RestorePoint } from '@/layers/restore-wizard/types'

type SnapshotRepository = components['schemas']['SnapshotRepository']
type SnapshotProject = components['schemas']['SnapshotProject']

/**
 * `repo-wz-2`/`access-wz-2`/`ci-wz-2`/`settings-wz-2`/`review-wz-2`/`project-wz-2`/`ref-wz-2` — the
 * `repositories` role's shared pane, new 2026-09-16. **A UX change, not a fidelity fix — the user's
 * own direct request, and `Prototype_v2.html` was changed to match rather than the other way
 * round.** Splits what `SelectRestorePointPane` used to combine on one pane (restore point,
 * *and* which repositories/projects) into two: this pane owns only the second half, reading
 * `jobListSnapshotRepositories`/`jobListSnapshotProjects` against the restore point already chosen.
 * Absent entirely for `WORKSPACE`-form kinds (`wshook`) — nothing to list, so `RestoreWizardLayer`
 * never mounts this pane for that one machine.
 */
export function SelectRepositoriesPane({
  config,
  heading,
  restorePoint,
  onNext,
  onBack,
}: {
  config: RestoreWizardConfig
  heading: string
  restorePoint: RestorePoint
  onNext: (args: { repositoryIds?: readonly string[]; projectKeys?: readonly string[]; repositoryId?: string; repositoryLabel?: string }) => void
  onBack: () => void
}) {
  const orgId = useActiveOrgId()!
  const workspaceId = useActiveWorkspaceId()!

  const [checkedRepos, setCheckedRepos] = useState<ReadonlySet<string>>(new Set())
  const [checkedProjects, setCheckedProjects] = useState<ReadonlySet<string>>(new Set())
  const [singleRepo, setSingleRepo] = useState<SnapshotRepository | undefined>()
  const objectsPager = useCursorPager()

  const jobId = restorePoint.data_snapshot.job_id

  useEffect(() => {
    objectsPager.reset()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId])

  const repositories = useQuery({
    queryKey: ['snapshot-repositories', orgId, workspaceId, jobId, objectsPager.cursor],
    enabled: config.targetScopeForm === 'REPOSITORIES',
    queryFn: async () => {
      const client = getApiClient()
      const { data, error } = await client.GET('/orgs/{org_id}/workspaces/{workspace_id}/snapshots/{job_id}/repositories', {
        params: { path: { org_id: orgId, workspace_id: workspaceId, job_id: jobId }, query: { cursor: objectsPager.cursor } },
      })
      if (error) throw new ApiProblemError(error as Problem)
      return data
    },
  })
  const repositoryRows = (repositories.data?.items ?? []) as unknown as readonly SnapshotRepository[]

  const projects = useQuery({
    queryKey: ['snapshot-projects', orgId, workspaceId, jobId, objectsPager.cursor],
    enabled: config.targetScopeForm === 'PROJECTS',
    queryFn: async () => {
      const client = getApiClient()
      const { data, error } = await client.GET('/orgs/{org_id}/workspaces/{workspace_id}/snapshots/{job_id}/projects', {
        params: { path: { org_id: orgId, workspace_id: workspaceId, job_id: jobId }, query: { cursor: objectsPager.cursor } },
      })
      if (error) throw new ApiProblemError(error as Problem)
      return data
    },
  })
  const projectRows = (projects.data?.items ?? []) as unknown as readonly SnapshotProject[]

  const problem = (repositories.error instanceof ApiProblemError ? repositories.error.problem : undefined) ?? (projects.error instanceof ApiProblemError ? projects.error.problem : undefined)
  const scopeChosen = config.singleRef ? Boolean(singleRepo) : config.targetScopeForm === 'REPOSITORIES' ? checkedRepos.size > 0 : checkedProjects.size > 0

  const nextUnitId = config.singleRef ? config.unitIds.selectRef! : config.unitIds.targets

  return (
    <div data-scr={brandUnitId(config.unitIds.repositories!)} data-cmp="pane">
      <WizardHeading>{heading}</WizardHeading>
      <WizardProse>
        {config.singleRef
          ? 'A single-ref restore narrows to exactly one repository — its branches and tags are picked on the next step.'
          : config.targetScopeForm === 'PROJECTS'
            ? 'Every project this snapshot captured — pick the ones this restore covers.'
            : 'Every repository this snapshot captured — pick the ones this restore covers.'}
      </WizardProse>

      {problem && (isUnexpectedRefusal(problem.refusal_kind) ? <UnexpectedRefusal problem={problem} /> : <Note>{problem.title}</Note>)}

      {config.targetScopeForm === 'REPOSITORIES' && !config.singleRef && (
        <div data-cmp="panel" className="bg-panel border border-bd rounded-2 mb-7">
          <div data-cmp="panel-header" className="p-5 border-b border-bd font-semibold text-body-lg">Repositories</div>
          <GridTable
            columns={[
              { key: 'name', header: 'Repository', render: (r) => r.name },
              { key: 'project', header: 'Project', render: (r) => <span className="text-muted">{r.project_key ?? '—'}</span> },
              { key: 'branches', header: 'Branches', render: (r) => r.branch_count },
              { key: 'commits', header: 'Commits', render: (r) => r.commit_count },
              { key: 'returns', header: 'Returns', render: (r) => formatBytes(r.content_bytes) },
              { key: 'captured', header: 'Last captured', render: (r) => (r.completed_at ? formatJobTime(r.completed_at) : '—') },
            ]}
            rows={repositoryRows}
            getRowKey={(r) => r.repository_id}
            selection={{
              selectedKeys: checkedRepos,
              onToggle: (key) => {
                const next = new Set(checkedRepos)
                if (next.has(key)) next.delete(key)
                else next.add(key)
                setCheckedRepos(next)
              },
            }}
          />
          <div data-cmp="grid-footer" className="border-t border-bd flex items-center justify-between p-5">
            <Note>Paged by cursor only — no total is served for this list.</Note>
            <Pager
              hasPrev={objectsPager.hasPrev}
              hasNext={Boolean(repositories.data?.next_cursor)}
              loading={repositories.isFetching}
              onPrev={objectsPager.goPrev}
              onNext={() => {
                if (repositories.data?.next_cursor) objectsPager.goNext(repositories.data.next_cursor)
              }}
            />
          </div>
        </div>
      )}

      {config.targetScopeForm === 'REPOSITORIES' && config.singleRef && (
        <div data-cmp="panel" className="bg-panel border border-bd rounded-2 mb-7">
          <div data-cmp="panel-header" className="p-5 border-b border-bd font-semibold text-body-lg">Repository</div>
          <GridTable
            columns={[
              { key: 'pick', header: '', render: (r) => <input type="radio" name="single-repo" checked={singleRepo?.repository_id === r.repository_id} onChange={() => setSingleRepo(r)} /> },
              { key: 'name', header: '', render: (r) => r.name },
            ]}
            rows={repositoryRows}
            getRowKey={(r) => r.repository_id}
            showHeader={false}
          />
          <div data-cmp="grid-footer" className="border-t border-bd flex items-center justify-between p-5">
            <Note>Paged by cursor only — no total is served for this list.</Note>
            <Pager
              hasPrev={objectsPager.hasPrev}
              hasNext={Boolean(repositories.data?.next_cursor)}
              loading={repositories.isFetching}
              onPrev={objectsPager.goPrev}
              onNext={() => {
                if (repositories.data?.next_cursor) objectsPager.goNext(repositories.data.next_cursor)
              }}
            />
          </div>
        </div>
      )}

      {config.targetScopeForm === 'PROJECTS' && (
        <div data-cmp="panel" className="bg-panel border border-bd rounded-2 mb-7">
          <div data-cmp="panel-header" className="p-5 border-b border-bd font-semibold text-body-lg">Projects</div>
          <GridTable
            columns={[
              { key: 'name', header: '', render: (p) => p.name },
              { key: 'count', header: '', render: (p) => <span className="text-muted">{p.repository_count} repositories</span> },
            ]}
            rows={projectRows}
            getRowKey={(p) => p.project_key}
            selection={{
              selectedKeys: checkedProjects,
              onToggle: (key) => {
                const next = new Set(checkedProjects)
                if (next.has(key)) next.delete(key)
                else next.add(key)
                setCheckedProjects(next)
              },
            }}
            showHeader={false}
          />
          <div data-cmp="grid-footer" className="border-t border-bd flex items-center justify-between p-5">
            <Note>Paged by cursor only — no total is served for this list.</Note>
            <Pager
              hasPrev={objectsPager.hasPrev}
              hasNext={Boolean(projects.data?.next_cursor)}
              loading={projects.isFetching}
              onPrev={objectsPager.goPrev}
              onNext={() => {
                if (projects.data?.next_cursor) objectsPager.goNext(projects.data.next_cursor)
              }}
            />
          </div>
        </div>
      )}

      <WizardButtons>
        <Button data-act={actionId(`nav.${config.unitIds.select}`)} ghost onClick={onBack}>
          Back
        </Button>
        <Button
          data-act={actionId(`nav.${nextUnitId}`)}
          disabled={!scopeChosen}
          onClick={() => {
            if (config.singleRef) {
              onNext({ repositoryId: singleRepo?.repository_id, repositoryLabel: singleRepo?.name })
              return
            }
            onNext({
              repositoryIds: config.targetScopeForm === 'REPOSITORIES' ? [...checkedRepos] : undefined,
              projectKeys: config.targetScopeForm === 'PROJECTS' ? [...checkedProjects] : undefined,
            })
          }}
        >
          Next
        </Button>
      </WizardButtons>
    </div>
  )
}
